import { Controller } from "@hotwired/stimulus"

/**
 * Status Controller
 *
 * Fetches status from Instatus and displays a custom status indicator.
 * Clicking the indicator links to the status page.
 */
export default class extends Controller<HTMLElement> {
  static values = {
    url: { type: String, default: "https://lumenfall.instatus.com/summary.json" },
    statusPage: { type: String, default: "https://status.lumenfall.ai" },
  }

  static targets = ["indicator", "text"]

  declare urlValue: string
  declare statusPageValue: string
  declare readonly indicatorTarget: HTMLElement
  declare readonly textTarget: HTMLElement
  declare readonly hasIndicatorTarget: boolean
  declare readonly hasTextTarget: boolean

  connect() {
    this.fetchStatus()
  }

  async fetchStatus() {
    try {
      const response = await fetch(this.urlValue)
      if (!response.ok) throw new Error("Failed to fetch status")

      const data = await response.json()
      this.updateDisplay(data)
    } catch (error) {
      // On error, show a neutral state
      this.showStatus("unknown", "Status")
    }
  }

  private updateDisplay(data: {
    page: { status: string }
    activeIncidents: Array<{ name: string; impact: string }>
    activeMaintenances: Array<{ name: string }>
  }) {
    const pageStatus = data.page.status

    switch (pageStatus) {
      case "UP":
        this.showStatus("up", "All systems operational")
        break
      case "HASISSUES":
        const incident = data.activeIncidents[0]
        const impactText = incident?.impact === "MAJOROUTAGE" ? "Major outage" : "Degraded performance"
        this.showStatus("issues", impactText)
        break
      case "UNDERMAINTENANCE":
        this.showStatus("maintenance", "Under maintenance")
        break
      default:
        this.showStatus("unknown", "Status")
    }
  }

  private showStatus(status: "up" | "issues" | "maintenance" | "unknown", text: string) {
    if (this.hasTextTarget) {
      this.textTarget.textContent = text
    }

    if (this.hasIndicatorTarget) {
      // Remove all status classes
      this.indicatorTarget.classList.remove(
        "bg-success",
        "bg-warning",
        "bg-error",
        "bg-base-content/30",
        "animate-pulse"
      )

      // Add appropriate class based on status
      switch (status) {
        case "up":
          this.indicatorTarget.classList.add("bg-success")
          break
        case "issues":
          this.indicatorTarget.classList.add("bg-error", "animate-pulse")
          break
        case "maintenance":
          this.indicatorTarget.classList.add("bg-warning", "animate-pulse")
          break
        default:
          this.indicatorTarget.classList.add("bg-base-content/30")
      }
    }
  }
}
